<template>
  <FlotoDrawerForm :open="open" @submit="handleSubmit" @cancel="handleCancel">
    <template v-slot:header>
      {{ $tc('add') }} {{ __tc('relationship') }} {{ $t('to') }}
      {{ resource.name }}
    </template>

    <MRow>
      <MCol :size="12">
        <FlotoFormItem
          id="relationship-type-radio"
          :label="$tc('relationship_type')"
          rules="required"
        >
          <MRadioGroup
            v-model="formData.relationshipType"
            as-button
            :options="relationshipTypeOptions"
          />
        </FlotoFormItem>
      </MCol>
      <MCol :size="6">
        <FlotoFormItem
          id="relation-picker"
          :label="$tc('relation')"
          rules="required"
        >
          <FlotoDropdownPicker
            v-model="formData.relationId"
            searchable
            as-input
            :options="relationOptions"
          />
        </FlotoFormItem>
      </MCol>
      <MCol :size="6">
        <FlotoFormItem
          id="target-type-picker"
          :label="__tc('target_type')"
          rules="required"
        >
          <FlotoDropdownPicker
            v-model="formData.destinationModel"
            searchable
            as-input
            :options="targetTypeOptions"
            @change="($event) => $emit('target-type-changed', $event)"
          />
        </FlotoFormItem>
      </MCol>
    </MRow>

    <!-- <MDivider class="my-1" /> -->

    <MRow class="min-h-0 flex-1" :gutter="0">
      <MCol
        :size="12"
        class="flex"
        :class="{
          '-mx':
            ['technician', 'requester'].indexOf(formData.destinationModel) ===
            -1,
        }"
      >
        <UserList
          v-if="
            ['technician', 'requester'].indexOf(formData.destinationModel) >= 0
          "
          :key="`${renderCount}-${formData.destinationModel}`"
          :type="formData.destinationModel"
          selectable
          searchable
          :exclude-items-by-id="uniq(excludedIdsByModuleName)"
          :hidden-columns="userHiddenColumns"
          @selection-change="handleTargetSelection"
        />
        <AssetList
          v-else-if="
            [
              $constants.ASSET_HARDWARE,
              $constants.ASSET_SOFTWARE,
              $constants.ASSET_NON_IT,
              $constants.ASSET_CONSUMABLE,
              $constants.CMDB,
            ].indexOf(formData.destinationModel) >= 0
          "
          ref="assetListRef"
          :key="`${renderCount}-${formData.destinationModel}`"
          :module-name="formData.destinationModel"
          selectable
          searchable
          :immediate="false"
          :exclude-items-by-id="uniq(excludedIdsByModuleName)"
          :hidden-columns="assetHiddenColumns"
          @selection-change="handleTargetSelection"
        />
        <UserGroupList
          v-else-if="['user_group'].indexOf(formData.destinationModel) >= 0"
          :key="renderCount"
          type="technician"
          :exclude-items-by-id="uniq(excludedIdsByModuleName)"
          selectable
          @selection-change="handleTargetSelection"
        />
        <DepartmentList
          v-else-if="
            [$constants.DEPARTMENT].indexOf(formData.destinationModel) >= 0
          "
          :key="renderCount"
          selectable
          :exclude-items-by-id="uniq(excludedIdsByModuleName)"
          @selection-change="handleTargetSelection"
        />
      </MCol>
    </MRow>

    <template v-slot:actions="{ submit, hide }">
      <MRow>
        <MCol :size="12" class="text-right">
          <MButton
            id="add-btn"
            class="mx-1"
            :loading="processing"
            @click="submit"
            >{{ $tc('add') }}</MButton
          >
          <MButton id="cancel-btn" variant="default" @click="hide">
            {{ $tc('cancel') }}
          </MButton>
        </MCol>
      </MRow>
    </template>
  </FlotoDrawerForm>
</template>

<script>
import UserList from '@components/item-selection-list/user-list'
import UserGroupList from '@components/item-selection-list/user-group-list'
import AssetList from '@components/item-selection-list/asset-list'
import DepartmentList from '@components/item-selection-list/department-list'
import { PreferenceComputed } from '@state/modules/preference'
import { LicenseComputed } from '@state/modules/license'
import uniq from 'lodash/uniq'

export default {
  name: 'AddRelationForm',
  components: { UserList, AssetList, UserGroupList, DepartmentList },
  inject: { AssetRelationshipContext: { default: { relationshipTypes: [] } } },
  props: {
    createFn: { type: Function, required: true },
    resource: { type: Object, required: true },
    excludedIdsMap: {
      type: Object,
      required: true,
    },
  },
  data() {
    this.uniq = uniq
    this.assetHiddenColumns = [
      'impactId',
      'version',
      'swTypeId',
      'hostName',
      'ipAddress',
      'assignedUserIds',
      'managedByGroupId',
      'managedById',
    ]
    this.userHiddenColumns = ['department', 'location']
    this.relationshipTypeOptions = [
      { value: 'direct', text: this.__tc('direct') },
      { value: 'inverse', text: this.__tc('inverse') },
    ]
    return {
      open: true,
      processing: false,
      formData: {
        relationshipType: 'direct',
      },
      renderCount: 0,
    }
  },
  computed: {
    ...PreferenceComputed,
    ...LicenseComputed,
    hasCmdbModule() {
      return this.availableModulesInLicense.indexOf(this.$constants.CMDB) >= 0
    },
    hasAssetModule() {
      return this.availableModulesInLicense.indexOf(this.$constants.ASSET) >= 0
    },
    excludedIdsByModuleName() {
      if (
        ['technician', 'requester'].indexOf(this.formData.destinationModel) >= 0
      ) {
        return this.excludedIdsMap[this.$constants.USER]
      }
      return this.excludedIdsMap[this.formData.destinationModel]
    },
    targetTypeOptions() {
      return [
        ...(this.myAllowedModules.indexOf('asset.hardware_software_asset') >=
          0 && this.hasAssetModule
          ? [
              {
                key: this.$constants.ASSET_HARDWARE,
                text: this.$tc('asset_hardware'),
              },
              {
                key: this.$constants.ASSET_SOFTWARE,
                text: this.$tc('asset_software'),
              },
            ]
          : []),
        ...((this.myAllowedModules || []).indexOf('asset.non_it_asset') >= 0 &&
        this.hasAssetModule
          ? [
              {
                key: this.$constants.ASSET_NON_IT,
                text: this.$tc('asset_non_it'),
              },
            ]
          : []),
        ...((this.myAllowedModules || []).indexOf('asset.consumable_asset') >=
          0 && this.hasAssetModule
          ? [
              {
                key: this.$constants.ASSET_CONSUMABLE,
                text: this.$tc('asset_consumable'),
              },
            ]
          : []),
        ...(this.hasCmdbModule
          ? [
              {
                key: this.$constants.CMDB,
                text: this.$tc('ci'),
              },
            ]
          : []),
        { key: this.$constants.DEPARTMENT, text: this.$tc('department') },
        { key: 'technician', text: this.$tc('technician') },
        { key: 'requester', text: this.$tc('requester') },
        { key: 'user_group', text: `${this.$tc('user')} ${this.$tc('group')}` },
      ]
    },
    relationOptions() {
      const type = this.formData.relationshipType
      const options = this.AssetRelationshipContext.relationshipTypes
      const prop = type === 'inverse' ? 'inverseName' : 'directName'
      return options.map((o) => ({ key: o.id, text: o[prop] }))
    },
  },
  watch: {
    'formData.destinationModel': function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.formData = {
          ...this.formData,
          destinationId: undefined,
        }
      }
    },
    excludedIdsByModuleName: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.renderCount = this.renderCount + 1
        }
      },
    },
  },
  methods: {
    handleTargetSelection($event) {
      this.formData = {
        ...this.formData,
        destinationId: $event.map((i) => i.id),
      }
    },
    handleSubmit() {
      if (!this.formData.destinationId) {
        this.$errorNotification({
          message: this.$t('error'),
          description: this.__tc('target_required'),
        })
        return
      }
      this.processing = true
      this.createFn(this.formData)
        .then(() => {
          this.open = false
          setTimeout(() => {
            this.$emit('hide')
          }, 400)
        })
        .finally(() => {
          this.processing = false
        })
    },
    handleCancel() {
      this.open = false
      setTimeout(() => {
        this.$emit('hide')
      }, 400)
    },
  },
}
</script>
